import { useState } from 'react';
import {
  Modal,
  Stack,
  Text,
  Alert,
  Select,
  Button,
  Group,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

type CancelTrialModalProps = {
  opened: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
  isCanceling: boolean;
  // Pre-formatted trial end date, e.g. "Aug 19, 2026". Omitted just drops the
  // date from the copy rather than showing a broken sentence.
  trialEndsOn?: string;
};

// Deliberately includes the misconception itself ("I don't want to be charged")
// as an option: if that's what most people pick, the fix is our copy, not a
// feature.
const REASON_OPTIONS = [
  { value: 'not_charged', label: "I don't want to be charged" },
  { value: 'not_using', label: "I'm not using it enough" },
  { value: 'missing_features', label: "It's missing something I need" },
  { value: 'too_expensive', label: 'Too expensive for me' },
  { value: 'not_right_time', label: 'Not the right time' },
  { value: 'other', label: 'Something else' },
];

const labelFor = (value: string | null) =>
  REASON_OPTIONS.find((option) => option.value === value)?.label;

/**
 * Confirmation step for "cancel trial".
 *
 * The modal is the actual feature: most people who look for a cancel button are
 * really asking "am I about to be charged?", so the answer is the first thing
 * they see, before they decide anything.
 */
export const CancelTrialModal = ({
  opened,
  onClose,
  onConfirm,
  isCanceling,
  trialEndsOn,
}: CancelTrialModalProps) => {
  const [reason, setReason] = useState<string | null>(null);

  const handleClose = () => {
    setReason(null);
    onClose();
  };

  const handleConfirm = () => {
    // Send the human-readable label rather than the enum value. It lands in a
    // notification email and on the user doc, where the label reads better.
    onConfirm(labelFor(reason));
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={<Text fw={600} size="lg">Cancel your free trial?</Text>}
      size="md"
      centered
      radius="lg"
    >
      <Stack gap="md">
        <Alert icon={<IconInfoCircle size={16} />} color="blue" radius="lg">
          <Text size="sm" fw={600}>You won't be charged either way.</Text>
          <Text size="sm">
            We never collected a card for your trial.
          </Text>
        </Alert>

        <Text size="sm">
          Canceling stops the trial emails. You keep full access
          {trialEndsOn ? ` until ${trialEndsOn}` : ' until your trial ends'},
          and you can resume any time before then.
        </Text>

        <Select
          label="Why are you canceling? (optional)"
          placeholder="Choose a reason"
          data={REASON_OPTIONS}
          value={reason}
          onChange={setReason}
          clearable
        />

        <Group justify="flex-end" gap="sm" mt="xs">
          <Button variant="subtle" color="gray" onClick={handleClose} disabled={isCanceling}>
            Never mind
          </Button>
          <Button color="red" variant="outline" onClick={handleConfirm} loading={isCanceling}>
            Cancel my trial
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
